import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

type DialogFieldControl = HTMLElement & Pick<HTMLInputElement, "value" | "disabled" | "required" | "checkValidity">;

const textControls = ".input:not([disabled]), .textarea:not([disabled])";

function setInvalid(field: HTMLElement, message: string) {
  field.classList.toggle("is-invalid", Boolean(message));
  const control = field.querySelector<HTMLElement>(".input, .textarea");
  if (message) control?.setAttribute("aria-invalid", "true");
  else control?.removeAttribute("aria-invalid");
  const error = field.querySelector(".fld-error span");
  if (error) error.textContent = message;
}

export function clearDialogFieldError(target: HTMLElement) {
  const field = target.closest<HTMLElement>(".fld");
  if (field?.classList.contains("is-invalid") && field.querySelector(".input, .textarea")) setInvalid(field, "");
}

export function validateDialogForm(form: HTMLFormElement): boolean {
  let first: DialogFieldControl | null = null;
  form.querySelectorAll<HTMLElement>(".fld").forEach(field => {
    const control = field.querySelector<DialogFieldControl>(".input, .textarea");
    if (!control || control.disabled) return;
    const message = control.required && !control.value.trim() ? control.dataset.msgRequired ?? "This field is required."
      : control.value.trim() && !control.checkValidity() ? control.dataset.msgInvalid ?? "Check this value." : "";
    setInvalid(field, message);
    if (message && !first) first = control;
  });
  if (first) { (first as HTMLElement).focus(); return false; }
  const invalidUpload = form.querySelector<HTMLInputElement>(".fld.is-invalid .upload input");
  if (invalidUpload) { invalidUpload.focus(); return false; }
  return true;
}

/** React adaptation of the supplied SBDialog lifecycle; native dialog owns modality. */
export function Dialog({ children, label, description, dirty, busy, ready = true, submitLabel, onSubmit, onClose, restoreFocusRef, size, className = "", headerLeading, footerLeading, showClose = true, showCancel = true, explicitDiscard = false }: {
  children: ReactNode;
  label: string;
  description?: ReactNode;
  dirty: boolean;
  busy: boolean;
  ready?: boolean;
  submitLabel: string;
  onSubmit(): Promise<boolean>;
  onClose(): void;
  restoreFocusRef: RefObject<HTMLElement | null>;
  size?: "sm" | "lg";
  className?: string;
  headerLeading?: ReactNode;
  footerLeading?: ReactNode;
  showClose?: boolean;
  showCancel?: boolean;
  explicitDiscard?: boolean;
}) {
  const id = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const closingRef = useRef(false);
  const disposedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeCallback = useRef(onClose);
  closeCallback.current = onClose;
  const [guard, setGuard] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  function focusEditor() {
    const dialog = dialogRef.current;
    const target = window.matchMedia("(pointer: coarse)").matches ? titleRef.current
      : dialog?.querySelector<HTMLElement>("[autofocus]") ?? dialog?.querySelector<HTMLElement>(textControls) ?? titleRef.current;
    target?.focus({ preventScroll: true });
  }

  useLayoutEffect(() => {
    disposedRef.current = false;
    openerRef.current = restoreFocusRef.current ?? document.activeElement as HTMLElement;
    const dialog = dialogRef.current!;
    dialog.showModal();
    titleRef.current?.focus({ preventScroll: true });
    return () => {
      disposedRef.current = true;
      clearTimeout(timerRef.current);
      if (dialog.open) dialog.close();
    };
  }, [restoreFocusRef]);

  useEffect(() => { if (ready && (!dialogRef.current?.contains(document.activeElement) || document.activeElement === titleRef.current)) focusEditor(); }, [ready]);
  useEffect(() => { if (guard) keepRef.current?.focus({ preventScroll: true }); }, [guard]);

  function finishClose(result: string) {
    const dialog = dialogRef.current;
    if (!dialog?.open) return;
    clearTimeout(timerRef.current);
    dialog.classList.remove("is-closing");
    closingRef.current = false;
    dialog.close(result);
  }

  function close(result: string) {
    const dialog = dialogRef.current;
    if (!dialog?.open || closingRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { finishClose(result); return; }
    closingRef.current = true;
    dialog.returnValue = result;
    dialog.classList.add("is-closing");
    timerRef.current = setTimeout(() => finishClose(result), 400);
  }

  function dismiss(outside = false) {
    if (busy || guard || closingRef.current) return;
    if (dirty && explicitDiscard && outside) return;
    if (dirty && !explicitDiscard) setGuard(true);
    else close("cancel");
  }

  const downOnBackdrop = useRef(false);
  return createPortal(<dialog ref={dialogRef} className={`dlg ${className}`.trim()} data-size={size} data-guard={guard ? "" : undefined}
    aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-desc` : undefined}
    onCancel={event => { event.preventDefault(); if (busy) return; if (guard) { setGuard(false); focusEditor(); } else dismiss(); }}
    onPointerDown={event => { downOnBackdrop.current = event.target === event.currentTarget; }}
    onClick={event => { if (event.target === event.currentTarget && downOnBackdrop.current) dismiss(true); }}
    onAnimationEnd={event => { if (event.target === event.currentTarget && closingRef.current) finishClose(event.currentTarget.returnValue); }}
    onClose={() => {
      if (disposedRef.current || dialogRef.current?.open) return;
      closeCallback.current();
      requestAnimationFrame(() => { const opener = restoreFocusRef.current ?? openerRef.current; if (opener?.isConnected) opener.focus({ preventScroll: true }); });
    }}>
    <form method="dialog" noValidate onInput={event => {
      clearDialogFieldError(event.target as HTMLElement);
    }} onSubmit={async event => {
      event.preventDefault();
      if (busy || !ready || guard || closingRef.current) return;
      if (!validateDialogForm(event.currentTarget)) return;
      if (await onSubmit()) close("submit");
    }}>
      <header className="dlg-head">
        {headerLeading}
        <h2 ref={titleRef} className="dlg-title" id={`${id}-title`} tabIndex={-1}>{label}</h2>
        {description ? <p className="dlg-desc" id={`${id}-desc`}>{description}</p> : null}
        {showClose ? <IconButton appearance="dialog" className="dlg-close" aria-label="Close" disabled={busy} onClick={() => dismiss()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>
        </IconButton> : null}
      </header>
      <div className={`dlg-body${scrolled ? " is-scrolled" : ""}`} onScroll={event => setScrolled(event.currentTarget.scrollTop > 0)}>{children}</div>
      <footer className="dlg-foot">
        {footerLeading ? <div className="dlg-foot-leading">{footerLeading}</div> : null}
        {showCancel ? <Button appearance="dialog" variant="quiet" data-dlg-cancel disabled={busy} onClick={() => dismiss()}>Cancel</Button> : null}
        <Button appearance="dialog" type="submit" loading={busy} disabled={!ready}>{submitLabel}</Button>
        <div className="dlg-guard">
          <p id={`${id}-guard`}>Discard your changes?</p>
          <Button ref={keepRef} appearance="dialog" variant="quiet" data-guard-keep aria-describedby={`${id}-guard`} onClick={() => { setGuard(false); focusEditor(); }}>Keep editing</Button>
          <Button appearance="dialog" variant="destructive" data-guard-discard onClick={() => { setGuard(false); close("cancel"); }}>Discard</Button>
        </div>
      </footer>
    </form>
  </dialog>, document.body);
}

export function DialogError({ id, children }: { id: string; children?: ReactNode }) {
  return <p className="fld-error" id={id}><svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true"><circle cx="7" cy="7" r="6" fill="currentColor"/><path d="M7 3.8v3.7M7 9.6v.1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg><span>{children}</span></p>;
}
